import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { generatePath, useLocation, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { ChevronRightThin, CrossLarge, MicrophoneOutline, Trash } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { Meeting } from "@acto/taskpane/core/domain";
import type { Note } from "@acto/taskpane/core/domain";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import type { MeetingCancellationReason } from "@acto/gql/graphql";
import { useCancelMeetings } from "@acto/taskpane/core/data-access/meetings/useCancelMeetings";
import { useCancelledMeetings } from "@acto/taskpane/core/data-access/meetings/useCancelledMeetings";
import { useMeetingsAwaitingRecap } from "@acto/taskpane/core/data-access/meetings/useMeetingsAwaitingRecap";
import { useNotes, useNotesCount } from "@acto/taskpane/core/data-access";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { useCustomerWithActivityYears } from "@acto/taskpane/field-sales-2/data-access/useCustomerWithActivityYears";
import { useDirectionalNavigate, usePageEnterAnimation } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { TopNav } from "@acto/taskpane/field-sales-2/components/patterns/TopNav";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { YearFilterPill } from "@acto/taskpane/field-sales-2/components/patterns/YearFilterPill/YearFilterPill";
import { Card, RowBody, RowList, RowMeta, RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { AdjustReportsOverlay } from "@acto/taskpane/field-sales-2/components/patterns/recap/AdjustReportsOverlay";
import {
  BucketBody,
  BucketCard,
  DateBadge,
  HeaderMoreButton,
  ListCard,
  RecapCardHeader,
  RecapCardLabel,
  RecapList,
  RecapRowDivider,
  RecapRowItem,
  type RecapRow,
} from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapList";
import { RecapCreationFlow } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapCreationFlow";
import { RecapStaticView } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapStaticView";
import { useToast, TOAST_TRANSITION_DELAY } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { BottomNavShell, BottomNavActionPill } from "@acto/taskpane/field-sales-2/components/patterns/BottomNav";
import { ReportRow } from "./ReportRow";

// First page is generous so most customers' reports arrive in one fetch; the infinite-scroll
// sentinel loads further pages when a year genuinely exceeds it.
const RECAPS_PAGE_SIZE = 25;

const CURRENT_YEAR = new Date().getFullYear();

const Page = styled("main")(({ theme }) => ({
  flex: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  background: theme.fieldSalesTwo.colors.background,
}));

const Content = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[20],
  // Reserve the floating pill's height (published by BottomNavShell) so the last report clears it.
  padding: `0 ${theme.fieldSalesTwo.spacing[16]} var(--bottom-nav-opaque-height, ${theme.fieldSalesTwo.spacing[32]})`,
  position: "relative",
  zIndex: 1,
}));

const DeletedCard = styled(BucketCard)({
  // No flex gap: the collapsed body must not leave a blank band under the header.
  gap: 0,
});

const DeletedBody = styled(BucketBody)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[20],
}));

// Typography is set explicitly: a button resets inherited font styles.
const DeletedToggle = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  minHeight: theme.fieldSalesTwo.spacing[48],
  padding: `0 0 0 ${theme.fieldSalesTwo.spacing[24]}`,
  border: "none",
  background: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
  textAlign: "left",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
}));

const DeletedChevronSlot = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: theme.fieldSalesTwo.spacing[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const DeletedChevron = styled(ChevronRightThin)<{ open?: boolean }>(({ theme }) => ({
  width: theme.fieldSalesTwo.spacing[18],
  height: theme.fieldSalesTwo.spacing[18],
  transform: "rotate(90deg)",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  variants: [{ props: { open: true }, style: { transform: "rotate(-90deg)" } }],
}));

const DeletedHeader = styled(RecapCardHeader)({
  paddingLeft: 0,
});

const StatCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

const StatTitle = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const StatLabel = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const StatValue = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[30],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.1,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const EmptyReports = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

export function CustomerRecapsPage() {
  const t = useTranslations("fieldSalesTwo.recaps");
  const tc = useTranslations("fieldSalesTwo.recaps.customer");
  const tAdjust = useTranslations("fieldSalesTwo.recaps.adjust");
  const tNav = useTranslations("fieldSalesTwo.bottomNav");
  const navigate = useDirectionalNavigate();
  const location = useLocation();
  const { id = "" } = useParams<{ id: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  usePageEnterAnimation(contentRef);
  const track = useTrack();

  const { data: customer } = useCustomerWithActivityYears(id);
  const { data: cancelledMeetings, refetch: refetchCancelledMeetings } = useCancelledMeetings({ clientId: id });

  // The current year is always offered so today's recap is writable before one exists for it.
  const yearOptions = useMemo(() => {
    const years = new Set<number>([
      CURRENT_YEAR,
      ...(customer?.noteYears ?? []),
      ...(cancelledMeetings ?? []).map((meeting) => meeting.scheduledDate.year()),
    ]);
    return [...years].sort((a, b) => b - a);
  }, [customer?.noteYears, cancelledMeetings]);

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const effectiveYear =
    selectedYear != null && yearOptions.includes(selectedYear) ? selectedYear : (yearOptions[0] ?? CURRENT_YEAR);
  const yearRange = { startDate: `${effectiveYear}-01-01`, endDate: `${effectiveYear}-12-31` };

  // In-place overlays (no route), mirroring OpenRecapsPage: the creation flow for open visits
  // and the static read-only view for a past report.
  const [flowMeetings, setFlowMeetings] = useState<Meeting[] | null>(null);
  const [viewRecap, setViewRecap] = useState<Note | null>(null);
  // Ad-hoc recap launched from this page's own floating pill (vs. the route-state launch from the
  // customer view). Closing returns to this list, not all the way back to the overview.
  const [adHocOpen, setAdHocOpen] = useState(false);
  const { showToast } = useToast();
  // Whether the flow was auto-started from the overview banner (vs. opened from this page's list).
  // Closing a banner-launched flow returns to the overview; a list-launched one returns to the list.
  const [flowFromBanner, setFlowFromBanner] = useState(false);

  // The banner on the customer overview routes here with `autoStartRecap` to jump straight into
  // writing, skipping the list. Consumed once (so closing the flow lands on the list, not a loop).
  const [autoStartPending, setAutoStartPending] = useState(() =>
    Boolean((location.state as { autoStartRecap?: boolean } | null)?.autoStartRecap),
  );

  // Route-state contract: `true` from the "Neue Nachbereitung" pill, a seed object carrying the
  // marking date from mark-as-done. Both open the ad-hoc recap flow. Closing returns to the
  // overview rather than this screen.
  const autoStartAdHocRecap = (
    location.state as {
      autoStartAdHocRecap?: boolean | { scheduledDate?: string };
    } | null
  )?.autoStartAdHocRecap;
  const adHocSeed = typeof autoStartAdHocRecap === "object" ? autoStartAdHocRecap : undefined;

  // Open visits = meetings awaiting a recap. Uses the SAME query as the overview banner
  // (useMeetingsAwaitingRecap), which reconciles materialized meetings AND virtual recurring-series
  // occurrences server-side — the plain `meetings` query only expands series when given a dateRange,
  // so a recurring-only customer would show in the banner but land here empty. Year-scoped
  // (the pill filters all three sections); already past/today from the server.
  const {
    data: openMeetings,
    isPending: openMeetingsPending,
    refetch: refetchOpenMeetings,
  } = useMeetingsAwaitingRecap({ clientId: id });
  const { cancelMeetings, result: cancelResult } = useCancelMeetings();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [deletedExpanded, setDeletedExpanded] = useState(false);
  const cancelledForYear = useMemo(
    () => (cancelledMeetings ?? []).filter((meeting) => meeting.scheduledDate.year() === effectiveYear),
    [cancelledMeetings, effectiveYear],
  );
  const openForYear = useMemo(() => {
    const today = dayjs();
    return (openMeetings ?? []).filter(
      (m) => m.scheduledDate.year() === effectiveYear && !m.scheduledDate.isAfter(today, "day"),
    );
  }, [openMeetings, effectiveYear]);
  const cancellableForYear = useMemo(
    () => openForYear.filter((m) => m.scheduledDate.isBefore(dayjs(), "day")),
    [openForYear],
  );

  // Open the flow as soon as there's something open for the active year; otherwise fall through
  // to the list. The subtlety: the banner runs this exact useMeetingsAwaitingRecap query, so
  // navigating here hits a warm urql cache — data can arrive a render after the flag is read.
  // An empty `openForYear` at that moment may be that lag, not a real "nothing open" — so we DEFER
  // the give-up. If meetings land on the next commit the effect re-runs, opens the flow, and
  // cancels the fallback; if they never come, the list shows.
  useEffect(() => {
    if (!autoStartPending) return;
    if (openForYear.length > 0) {
      setFlowMeetings(openForYear);
      setFlowFromBanner(true);
      setAutoStartPending(false);
      return;
    }
    if (openMeetingsPending) return;
    const timer = setTimeout(() => setAutoStartPending(false), 0);
    return () => clearTimeout(timer);
  }, [autoStartPending, openMeetingsPending, openForYear]);

  const {
    data: recaps,
    isPending: recapsPending,
    isFetching: recapsFetching,
    hasNextPage,
    fetchNextPage,
    refetch: refetchRecaps,
  } = useNotes({ clientId: id, dateRange: yearRange, limit: RECAPS_PAGE_SIZE });
  const reports = recaps ?? [];

  const { count: reportsCount } = useNotesCount({ clientId: id, dateRange: yearRange });

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore: hasNextPage,
    isLoadingMore: recapsFetching && reports.length > 0,
    loadMore: fetchNextPage,
    resetKey: reports.length,
  });

  const handleClose = () => navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }), { direction: "pop" });

  // Tracked only when the LIST branch below is actually rendering — isListVisible mirrors every
  // early-return condition above (creation flow, static view, ad-hoc flow, pending auto-start), so a
  // mount that resolves straight into a flow fires nothing, and a later fallback to the list fires once.
  const isListVisible = !flowMeetings && !viewRecap && !autoStartAdHocRecap && !autoStartPending;
  useViewTrackedOnce(
    () => {
      if (!customer) return;
      track(AnalyticsEvents.RECAPS_VIEWED, { customerId: customer.id, customerName: customer.name });
    },
    isListVisible && customer ? customer.id : null,
  );

  if (flowMeetings) {
    // On close, refetch the year's recaps so a just-written report appears in Berichte and is
    // scoped to the correct year — the shared note-cache patch inserts at the top regardless of
    // the dateRange/type filter, so it can't be relied on for the year-filtered list. (The Offen
    // removal is handled by saveNoteUpdate dropping the meeting from hasNote:false queries.)
    const closeFlow = () => {
      // Banner shortcut: the user came straight from the overview and never wanted the list, so
      // exit all the way back. List-launched: return to the list with the new report merged in.
      if (flowFromBanner) {
        handleClose();
        return;
      }
      setFlowMeetings(null);
      refetchRecaps?.();
    };
    return <RecapCreationFlow meetings={flowMeetings} onClose={closeFlow} />;
  }

  if (viewRecap) {
    // Key by recap id so the detail's in-place edit state resets when a different report is opened.
    return (
      <RecapStaticView
        key={viewRecap.id}
        recap={viewRecap}
        customer={customer}
        onClose={() => setViewRecap(null)}
        onSaved={() => refetchRecaps?.()}
        onDeleted={() => {
          setViewRecap(null);
          showToast({
            message: t("edit.recapDeleted"),
            tone: "destructive",
            icon: <Trash aria-hidden="true" />,
            initialDelay: TOAST_TRANSITION_DELAY,
          });
        }}
      />
    );
  }

  // Hold a blank background until the customer resolves, then run the ad-hoc creation flow.
  if (autoStartAdHocRecap || adHocOpen) {
    if (!customer) return <Page />;
    const onCloseAdHoc = autoStartAdHocRecap
      ? handleClose
      : () => {
          setAdHocOpen(false);
          refetchRecaps?.();
        };
    return <RecapCreationFlow adHocCustomer={customer} adHocSeed={adHocSeed} onClose={onCloseAdHoc} />;
  }

  // Hold a blank background while the auto-start resolves so the list never flashes before the
  // creation flow takes over.
  if (autoStartPending) {
    return <Page />;
  }

  // The meetings query's client has no street; the page's full customer supplies it when available.
  const toRow = (meeting: Meeting): RecapRow => ({
    id: meeting.id,
    badge: { primary: meeting.scheduledDate.format("D"), secondary: meeting.scheduledDate.format("MMM").toUpperCase() },
    name: meeting.customer.name,
    address: [meeting.customer.externalReference, customer?.street].filter(Boolean).join(" · "),
  });

  // Prefer the exact server count; fall back to the loaded-page count (suffixed with "+" while
  // more pages exist) until it arrives.
  const totalLabel =
    reportsCount != null ? `${reportsCount}` : hasNextPage ? `${reports.length}+` : `${reports.length}`;

  const handleCancel = async (ids: string[], reason: MeetingCancellationReason, comment?: string) => {
    const cancelled = await cancelMeetings(
      cancellableForYear.filter((meeting) => ids.includes(meeting.id)),
      reason,
      comment,
    );
    if (!cancelled) return;

    setAdjustOpen(false);
    refetchOpenMeetings({ requestPolicy: "network-only" });
    refetchCancelledMeetings({ requestPolicy: "network-only" });
    showToast({
      message: tAdjust("deleted", { count: cancelled.length }),
      tone: "destructive",
      icon: <Trash aria-hidden="true" />,
      initialDelay: TOAST_TRANSITION_DELAY,
    });
  };

  return (
    <Page>
      <TopNav
        leading={
          <HeaderIconButton type="button" aria-label={t("close")} onClick={handleClose}>
            <CrossLarge width={18} height={18} />
          </HeaderIconButton>
        }
        trailing={
          yearOptions.length > 1 ? (
            <YearFilterPill selectedYear={effectiveYear} yearOptions={yearOptions} onChange={setSelectedYear} />
          ) : null
        }
      />

      <Content ref={contentRef}>
        <StatCard>
          <StatTitle>{t("title")}</StatTitle>
          <Divider />
          <div>
            <StatLabel>{tc("total")}</StatLabel>
            <StatValue>{totalLabel}</StatValue>
          </div>
        </StatCard>

        {openForYear.length > 0 && (
          <RecapList
            heading={tc("open")}
            headerAction={
              cancellableForYear.length > 0 ? (
                <MenuPopover
                  trigger={<HeaderMoreButton aria-label={tAdjust("moreActions")} />}
                  ariaLabel={tAdjust("moreActions")}
                  items={[{ label: tAdjust("menuLabel"), onSelect: () => setAdjustOpen(true) }]}
                />
              ) : undefined
            }
            rows={openForYear.map(toRow)}
            ctaLabel={t("completeReports", { count: openForYear.length })}
            onRowClick={(rowId) => {
              const meeting = openForYear.find((m) => m.id === rowId);
              if (meeting) setFlowMeetings([meeting]);
            }}
            onComplete={() => setFlowMeetings(openForYear)}
          />
        )}

        <BucketCard>
          <RecapCardHeader>
            <RecapCardLabel as="h2">{tc("reports")}</RecapCardLabel>
          </RecapCardHeader>
          <BucketBody>
            <ListCard>
              {reports.length === 0 ? (
                !recapsPending && <EmptyReports>{tc("reportsEmpty")}</EmptyReports>
              ) : (
                <RowList role="list">
                  {reports.map((recap, index) => (
                    <Fragment key={recap.id}>
                      <ReportRow
                        recap={recap}
                        onClick={() => {
                          track(AnalyticsEvents.RECAP_OPENED, { recapId: recap.id, customerId: id });
                          setViewRecap(recap);
                        }}
                      />
                      {index < reports.length - 1 && <RecapRowDivider />}
                    </Fragment>
                  ))}
                </RowList>
              )}
              {hasNextPage && <div ref={sentinelRef} aria-hidden="true" />}
            </ListCard>
          </BucketBody>
        </BucketCard>

        {cancelledForYear.length > 0 && (
          <DeletedCard>
            <DeletedHeader>
              <RecapCardLabel as="h2" id="deleted-reports-heading">
                <DeletedToggle
                  type="button"
                  aria-expanded={deletedExpanded}
                  aria-controls="deleted-reports"
                  onClick={() => setDeletedExpanded((expanded) => !expanded)}
                >
                  {tc("deleted")}
                  <DeletedChevronSlot aria-hidden="true">
                    <DeletedChevron open={deletedExpanded} strokeWidth={1.5} />
                  </DeletedChevronSlot>
                </DeletedToggle>
              </RecapCardLabel>
            </DeletedHeader>
            <Collapse open={deletedExpanded} id="deleted-reports" aria-labelledby="deleted-reports-heading">
              <DeletedBody>
                <ListCard>
                  <RowList role="list">
                    {cancelledForYear.map(toRow).map((row, index, all) => (
                      <Fragment key={row.id}>
                        <RecapRowItem>
                          <DateBadge>
                            <span>{row.badge.primary}</span>
                            {row.badge.secondary && <span>{row.badge.secondary}</span>}
                          </DateBadge>
                          <RowBody>
                            <RowTitle>{row.name}</RowTitle>
                            <RowMeta>{row.address}</RowMeta>
                          </RowBody>
                        </RecapRowItem>
                        {index < all.length - 1 && <RecapRowDivider />}
                      </Fragment>
                    ))}
                  </RowList>
                </ListCard>
              </DeletedBody>
            </Collapse>
          </DeletedCard>
        )}
      </Content>

      <AdjustReportsOverlay
        isOpen={adjustOpen}
        onOpenChange={setAdjustOpen}
        rows={cancellableForYear.map(toRow)}
        onConfirm={handleCancel}
        isSubmitting={cancelResult.fetching}
      />

      <BottomNavShell>
        <BottomNavActionPill onClick={() => setAdHocOpen(true)} disabled={!customer}>
          <MicrophoneOutline width={18} height={20} aria-hidden="true" />
          {tNav("newFollowUp")}
        </BottomNavActionPill>
      </BottomNavShell>
    </Page>
  );
}
